import { Button } from "../ui/button";
import { ArrowLeft, Mail, Smartphone } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { updateNotificationPreferences } from "@/api/notifications";
import { useNotificationsQuery } from "@/hooks/use-notifications-query";

interface Props {
  onClose: () => void;
}

export function AlertsPreferences({ onClose }: Props) {
  const { notificationsQuery, preferencesQuery } = useNotificationsQuery();

  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (method: "email" | "in-app" | "both") => {
      await updateNotificationPreferences({
        deliveryMethod: method,
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
    },
  });

  const isMethodChecked = (method: "email" | "in-app") => {
    return (
      preferencesQuery.data.deliveryMethod === method ||
      preferencesQuery.data.deliveryMethod === "both"
    );
  };

  const handleChange = async (method: "email" | "in-app") => {
    const newMethod = method === "email" ? "in-app" : "email";

    if (
      isMethodChecked(method) &&
      preferencesQuery.data.deliveryMethod === "both"
    ) {
      await mutateAsync(newMethod);
    } else if (
      !isMethodChecked(method) &&
      preferencesQuery.data.deliveryMethod === newMethod
    ) {
      await mutateAsync("both");
    }
  };

  return (
    <div>
      <div className="inline-flex w-full p-3 justify-start items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onClose}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h3 className="font-medium">Notification Preferences</h3>
      </div>
      <Separator />
      <div className="p-4 space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          Delivery Method
        </h4>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <Label
                htmlFor="email-notifications"
                className="text-sm font-medium"
              >
                Email Notifications
              </Label>
            </div>
          </div>
          <Switch
            id="email-notifications"
            checked={isMethodChecked("email")}
            disabled={isPending}
            onCheckedChange={() => handleChange("email")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
              <Smartphone className="h-4 w-4" />
            </div>
            <div>
              <Label
                htmlFor="in-app-notifications"
                className="text-sm font-medium"
              >
                In-App Notifications
              </Label>
            </div>
          </div>
          <Switch
            id="in-app-notifications"
            checked={isMethodChecked("in-app")}
            disabled={isPending}
            onCheckedChange={() => handleChange("in-app")}
          />
        </div>
      </div>
    </div>
  );
}
